import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Building2, Users, AlertCircle, TrendingUp, Activity,
  Clock, CheckCircle, XCircle, PauseCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const statusConfig = {
  trialing: { label: 'Trial', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  active: { label: 'Aktiv', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  past_due: { label: 'Überfällig', color: 'bg-amber-500/20 text-amber-400', icon: AlertCircle },
  cancelled: { label: 'Gekündigt', color: 'bg-slate-500/20 text-slate-400', icon: XCircle },
  suspended: { label: 'Gesperrt', color: 'bg-red-500/20 text-red-400', icon: PauseCircle },
  expired: { label: 'Abgelaufen', color: 'bg-red-500/20 text-red-400', icon: XCircle },
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentAccounts, setRecentAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('access_token');  // Korrigiert von cashx_token
    
    try {
      // Fetch accounts for stats
      const response = await fetch(`${API_URL}/api/admin/accounts?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Calculate stats
        const statusCounts = {};
        const planCounts = {};
        
        data.accounts.forEach(acc => {
          const status = acc.account_status || 'active';
          statusCounts[status] = (statusCounts[status] || 0) + 1;
          
          const plan = acc.plan || 'starter';
          planCounts[plan] = (planCounts[plan] || 0) + 1;
        });
        
        setStats({
          total: data.total,
          statusCounts,
          planCounts
        });
        
        setRecentAccounts(data.accounts.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 
          className="text-2xl md:text-3xl font-bold"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          data-testid="admin-dashboard-title"
        >
          Admin Dashboard
        </h1>
        <p className="text-slate-400 mt-1">Übersicht über alle Accounts und Aktivitäten</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <Card key={i} className="p-6" style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}>
              <Skeleton className="h-4 w-24 mb-2 bg-slate-700" />
              <Skeleton className="h-8 w-16 bg-slate-700" />
            </Card>
          ))
        ) : (
          <>
            <Card 
              className="p-6" 
              style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
              data-testid="stat-total-accounts"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Gesamt Accounts</p>
                  <p className="text-3xl font-bold mt-1">{stats?.total || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#2F80FF]/20 rounded-lg flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-[#2F80FF]" />
                </div>
              </div>
            </Card>
            
            <Card 
              className="p-6" 
              style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
              data-testid="stat-active-accounts"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Aktive Accounts</p>
                  <p className="text-3xl font-bold mt-1 text-emerald-400">
                    {stats?.statusCounts?.active || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-emerald-400" />
                </div>
              </div>
            </Card>
            
            <Card 
              className="p-6" 
              style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
              data-testid="stat-trial-accounts"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Im Trial</p>
                  <p className="text-3xl font-bold mt-1 text-blue-400">
                    {stats?.statusCounts?.trialing || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </Card>
            
            <Card 
              className="p-6" 
              style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
              data-testid="stat-attention-accounts"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Achtung erforderlich</p>
                  <p className="text-3xl font-bold mt-1 text-amber-400">
                    {(stats?.statusCounts?.past_due || 0) + 
                     (stats?.statusCounts?.suspended || 0) +
                     (stats?.statusCounts?.expired || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-amber-400" />
                </div>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Recent Accounts & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Accounts */}
        <Card 
          className="lg:col-span-2 p-6" 
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Neueste Accounts</h2>
            <Link 
              to="/admin/accounts"
              className="text-sm text-[#2F80FF] hover:underline"
            >
              Alle anzeigen
            </Link>
          </div>
          
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full bg-slate-700 rounded-lg" />
              ))}
            </div>
          ) : recentAccounts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Noch keine Accounts vorhanden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentAccounts.map((account) => {
                const config = statusConfig[account.account_status] || statusConfig.active;
                const StatusIcon = config.icon;
                
                return (
                  <Link
                    key={account.tenant_id}
                    to={`/admin/accounts/${account.tenant_id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-800/50 transition-colors"
                    data-testid={`recent-account-${account.tenant_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-slate-300" />
                      </div>
                      <div>
                        <p className="font-medium">{account.company_name}</p>
                        <p className="text-sm text-slate-400">{account.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${config.color}`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {config.label}
                      </Badge>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </Card>

        {/* Plan Distribution */}
        <Card 
          className="p-6" 
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <h2 className="text-lg font-semibold mb-4">Plan-Verteilung</h2>
          
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full bg-slate-700 rounded" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {['starter', 'professional', 'enterprise'].map((plan) => {
                const count = stats?.planCounts?.[plan] || 0;
                const total = stats?.total || 1;
                const percentage = Math.round((count / total) * 100) || 0;
                
                return (
                  <div key={plan}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm capitalize">{plan}</span>
                      <span className="text-sm text-slate-400">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          plan === 'enterprise' ? 'bg-amber-500' :
                          plan === 'professional' ? 'bg-[#2F80FF]' : 'bg-slate-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Quick Actions */}
          <div className="mt-6 pt-6 border-t border-slate-700">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Schnellaktionen</h3>
            <div className="space-y-2">
              <Link 
                to="/admin/accounts"
                className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
              >
                <Building2 className="h-4 w-4" />
                Accounts verwalten
              </Link>
              <Link 
                to="/admin/audit-log"
                className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
              >
                <Activity className="h-4 w-4" />
                Audit-Log einsehen
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
